import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

// Compact relative-time helper for the "Last Run" column.
// Returns "—" for null, "Xs ago" for <60s, "Xm ago" for <60m, "Xh ago" for <24h, locale date+time otherwise.
const formatRelativeTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (Number.isNaN(diffSec)) return '—';
  if (diffSec < 0) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// MetricCard component - moved outside to avoid re-creating on every render
const MetricCard = ({ card }) => (
  <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 overflow-hidden border border-gray-200 hover:border-purple-300">
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${card.bgColor} border border-gray-200`}>
          {card.icon}
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold ${card.textColor}`}>
            {card.value}
          </div>
        </div>
      </div>
      <h3 className="text-gray-700 font-semibold text-base">{card.title}</h3>
      {card.subtext && (
        <p className="text-xs text-gray-500 mt-1">{card.subtext}</p>
      )}
    </div>
  </div>
);

const SuperAdminDashboardPage = () => {
  const { currentUser, stats } = useOutletContext();
  
  // Platform storage state
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(true);
  
  // Scheduled jobs state
  const [jobsData, setJobsData] = useState(null);
  
  // Toast notification state
  const [notification, setNotification] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(true);
  
  // Job trigger modal state
  const [triggerModal, setTriggerModal] = useState({
    isOpen: false,
    job: null,
    confirmationInput: '',
    isTriggering: false
  });

  // Job descriptions for tooltips
  const jobDescriptions = {
    'auto_clock_out': 'Automatically clocks out team members who forgot to clock out at the end of their shift. Runs adaptively based on activity.',
    'session_cleanup': 'Removes expired and invalidated user sessions older than 90 days to maintain database hygiene and security.',
    'clock_reminders': 'Sends push notifications to team members reminding them to clock in/out based on their scheduled shifts.',
    'notification_cleanup': 'Deletes old notifications (older than 7 days) to keep the notification inbox clean and performant.',
    'check_scheduled_broadcasts': 'Checks for scheduled broadcast messages and sends them at their designated time.',
    'cleanup_stale_broadcast_locks': 'Removes stale locks on broadcast messages that may have been left behind due to server issues.',
    'monthly_neo_credits_allocation': 'Allocates monthly Neo AI credits to all active users based on their subscription tier on the 1st of each month.',
    'grace_period_check': 'Checks organisations with expired subscriptions and sends reminder emails. Also handles grace period expiration.',
    'pending_downgrade_check': 'Completes pending subscription downgrades when the billing cycle ends, removing the temporary access to higher-tier features.',
    'subscription_reconciliation': 'Syncs local subscription states with PayPal to detect and fix any drift between systems (e.g., cancelled subscriptions not reflected locally).',
    'flash_upgrade_expiry': 'Checks for expired flash upgrades and sends warning/expiry notification emails. Enforces tier limits when flash upgrades expire.',
    'pending_downgrade_reminders': 'Sends reminder emails 48 hours before pending downgrades take effect, allowing users to keep their current plan if desired.',
    'incomplete_upgrade_cleanup': 'Cleans up incomplete mid-subscription upgrades that timed out (e.g., PayPal approval abandoned). Issues refunds if payment was captured.',
    'pending_organisation_deletion': 'Processes organisations scheduled for deletion. Sends reminder emails at 10, 3, and 1 day(s) remaining. Permanently deletes organisations after 30-day grace period.',
    'overdue_expired_check': 'Automatically marks sent invoices as overdue when past their due date, and sent quotes as expired when past their valid_until date.',
    'scheduled_database_backup': 'Creates daily automatic backups of the entire database. Automatically cleans up old automatic backups (keeps 20 most recent).',
    'marketing_drip_processor': 'Processes all automated marketing email sequences: Welcome (5 emails), Onboarding (8 emails), and Re-engagement (3 emails). Throttled to max 1 email per sequence per user per run.',
    'mailgun_inbound_poller': 'Polls Mailgun for stored inbound support emails every 2 minutes. Creates new tickets or adds replies to existing ones. Replaces the inbound webhook.',
    'system_log_flusher': 'Flushes buffered application logs to MongoDB every 30 seconds. Powers the System Logs dashboard. Logs are retained for 7 days.',
    'team_onboarding_processor': 'Sends onboarding push notifications to team members over a 60-day programme (20 notifications). Delivered as broadcasts. Throttled to 1 per user per day.',
    'outbound_campaign_processor': 'Processes active outbound marketing campaigns every 15 minutes. Sends initial contact + follow-up emails to business contacts via Mailgun.',
    'outreach_inbox_poller': 'Polls Mailgun for inbound replies to outreach emails every 2 minutes. Matches replies to contacts and stores in the inbox.'
  };
  
  // Fetch platform storage usage
  const fetchStorageUsage = useCallback(async (forceRefresh = false) => {
    try {
      setStorageLoading(true);
      const url = forceRefresh 
        ? `${API}/super-admin/platform-storage?force_refresh=true`
        : `${API}/super-admin/platform-storage`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setStorageData(response.data);
      }
    } catch (error) {
      console.error('Error fetching platform storage usage:', error);
      setStorageData(null);
    } finally {
      setStorageLoading(false);
    }
  }, []);
  
  // Fetch scheduled jobs status
  const fetchJobsStatus = useCallback(async () => {
    try {
      setJobsLoading(true);
      const response = await axios.get(`${API}/super-admin/scheduled-jobs`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setJobsData(response.data);
      }
    } catch (error) {
      console.error('Error fetching scheduled jobs status:', error);
      setJobsData(null);
    } finally {
      setJobsLoading(false);
    }
  }, []);
  
  // Helper to show notification
  const showNotification = (type, title, description) => {
    setNotification({ type, title, description });
    // Auto-dismiss after 5 seconds
    setTimeout(() => setNotification(null), 5000);
  };
  
  // Handle job trigger
  const handleTriggerJob = (job) => {
    setTriggerModal({
      isOpen: true,
      job: job,
      confirmationInput: '',
      isTriggering: false
    });
  };
  
  // Execute job trigger
  const executeTrigger = async () => {
    const { job, confirmationInput } = triggerModal;
    
    setTriggerModal(prev => ({ ...prev, isTriggering: true }));
    
    try {
      const payload = job.requires_confirmation 
        ? { confirmation_phrase: confirmationInput }
        : {};
        
      const response = await axios.post(
        `${API}/super-admin/scheduled-jobs/${job.id}/trigger`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.success) {
        showNotification('success', `Job "${job.name}" executed successfully`, response.data.result || response.data.message);
        setTriggerModal({ isOpen: false, job: null, confirmationInput: '', isTriggering: false });
        // Refresh job statuses
        fetchJobsStatus();
      } else if (response.data.requires_confirmation) {
        showNotification('error', 'Confirmation required', response.data.message);
      }
    } catch (error) {
      console.error('Error triggering job:', error);
      showNotification('error', 'Failed to trigger job', error.response?.data?.detail || error.message);
    } finally {
      setTriggerModal(prev => ({ ...prev, isTriggering: false }));
    }
  };
  
  // Close modal
  const closeModal = () => {
    setTriggerModal({ isOpen: false, job: null, confirmationInput: '', isTriggering: false });
  };
  
  useEffect(() => {
    fetchStorageUsage();
    fetchJobsStatus();
  }, [fetchStorageUsage, fetchJobsStatus]);

  // Platform Metrics Cards
  const platformMetrics = [
    {
      title: 'App Installs',
      value: stats?.app_installs || 0,
      subtext: 'Unique mobile devices with app installed',
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-800'
    },
    {
      title: 'Active Clock-In Sessions',
      value: stats?.active_clock_sessions || 0,
      subtext: 'Users currently clocked in across all sites',
      icon: (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-green-50',
      textColor: 'text-green-800'
    },
    {
      title: 'Active Clock-In Locations',
      value: stats?.active_clock_locations || 0,
      subtext: 'Unique cities with active clock-ins',
      icon: (
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-800'
    }
  ];

  // Organisation Data Cards
  const organisationData = [
    {
      title: 'Organisations',
      value: stats?.total_organisations || 0,
      subtext: 'Total registered organisations',
      icon: (
        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-800'
    },
    {
      title: 'Users',
      value: stats?.total_users || 0,
      subtext: 'Total team members across all organisations',
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-800'
    },
    {
      title: 'Sites',
      value: stats?.total_sites || 0,
      subtext: 'Total work sites across all organisations',
      icon: (
        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-800'
    },
    {
      title: 'New Organisations',
      value: stats?.new_organisations || 0,
      subtext: 'New registrations in the last 30 days',
      icon: (
        <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          <circle cx="19" cy="7" r="3" fill="currentColor" stroke="white" strokeWidth={1.5} />
          <path d="M19 6v2M18 7h2" stroke="white" strokeWidth={1.5} strokeLinecap="round" />
        </svg>
      ),
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-800'
    },
    {
      title: 'New Users',
      value: stats?.new_users || 0,
      subtext: 'New registrations in the last 30 days',
      icon: (
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      ),
      bgColor: 'bg-teal-50',
      textColor: 'text-teal-800'
    },
    {
      title: 'Site Locations',
      value: stats?.site_locations || 0,
      subtext: 'Unique cities where sites are located',
      icon: (
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      bgColor: 'bg-red-50',
      textColor: 'text-red-800'
    }
  ];

  // System Information Cards
  const systemInfo = [
    {
      title: 'Database',
      value: stats?.database_name || 'N/A',
      icon: (
        <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-800'
    },
    {
      title: 'Backend Status',
      value: stats?.backend_uptime || 'Running',
      icon: (
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-green-50',
      textColor: 'text-green-800'
    },
    {
      title: 'Super Admin User',
      value: `@${currentUser?.username || 'N/A'}`,
      icon: (
        <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-800'
    },
    {
      title: 'Access Level',
      value: currentUser?.role?.toUpperCase() || 'N/A',
      icon: (
        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-800'
    }
  ];

  return (
    <div className="w-full space-y-8">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm w-full shadow-lg rounded-lg pointer-events-auto ${
          notification.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3 w-0 flex-1">
                <p className={`text-sm font-medium ${notification.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                  {notification.title}
                </p>
                {notification.description && (
                  <p className={`mt-1 text-sm ${notification.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {notification.description}
                  </p>
                )}
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  onClick={() => setNotification(null)}
                  className={`rounded-md inline-flex ${notification.type === 'success' ? 'text-green-400 hover:text-green-500' : 'text-red-400 hover:text-red-500'}`}
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">
              Welcome, {currentUser?.first_name}!
            </h1>
            <p className="text-purple-200 text-sm">System-wide administration dashboard</p>
          </div>
        </div>
      </div>

      {/* Section 1: Platform Metrics */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Platform Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {platformMetrics.map((card, index) => (
            <MetricCard key={index} card={card} />
          ))}
        </div>
      </div>

      {/* Section 2: Organisation Data */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h2a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h2m0 0V6a2 2 0 012-2h4a2 2 0 012 2v2m0 0h2m-6 0H8" />
          </svg>
          Organisation Data
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organisationData.map((card, index) => (
            <MetricCard key={index} card={card} />
          ))}
        </div>
      </div>

      {/* Section 3: System Information */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          System Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {systemInfo.map((card, index) => (
            <MetricCard key={index} card={card} />
          ))}
        </div>
      </div>

      {/* Section 4: Platform Storage Usage */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          Platform Storage
        </h2>
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6" data-testid="platform-storage-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-slate-100 border border-gray-200 mr-3">
                <svg className="w-8 h-8 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Google Cloud Storage Usage</h3>
                <p className="text-sm text-gray-500">Total file storage across all organisations</p>
              </div>
            </div>
            <button 
              onClick={() => fetchStorageUsage(true)}
              disabled={storageLoading}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh storage data"
            >
              <svg className={`w-4 h-4 mr-2 ${storageLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          
          {storageLoading && !storageData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : storageData?.storage ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Total Storage */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Total Storage Used</p>
                    <p className="text-3xl font-bold text-purple-800 mt-1">
                      {storageData.storage.display_value} <span className="text-xl">{storageData.storage.display_unit}</span>
                    </p>
                  </div>
                  <div className="p-3 bg-purple-200 rounded-full">
                    <svg className="w-6 h-6 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-purple-500 mt-2">
                  {storageData.storage.total_mb.toLocaleString()} MB total
                </p>
              </div>
              
              {/* Organisations with Data */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Organisations with Data</p>
                    <p className="text-3xl font-bold text-blue-800 mt-1">
                      {storageData.storage.org_count}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-200 rounded-full">
                    <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-blue-500 mt-2">
                  Active file storage
                </p>
              </div>
              
              {/* Last Updated */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Last Updated</p>
                    <p className="text-lg font-semibold text-gray-800 mt-1">
                      {new Date(storageData.last_updated).toLocaleTimeString('en-GB', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(storageData.last_updated).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-200 rounded-full">
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {storageData.storage.from_cache ? 'From cache' : 'Fresh calculation'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p>Unable to load storage data</p>
              <button 
                onClick={() => fetchStorageUsage(true)}
                className="mt-2 text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Section 5: Scheduled Jobs */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Scheduled Jobs
        </h2>
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden" data-testid="scheduled-jobs-section">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  jobsData?.scheduler_running ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  <span className={`w-2 h-2 mr-1.5 rounded-full ${
                    jobsData?.scheduler_running ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  Scheduler {jobsData?.scheduler_running ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {jobsData?.checked_at && (
                <span className="text-xs text-gray-500">
                  Last checked: {new Date(jobsData.checked_at).toLocaleTimeString('en-GB', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </span>
              )}
              <button 
                onClick={fetchJobsStatus}
                disabled={jobsLoading}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Refresh job statuses"
              >
                <svg className={`w-4 h-4 mr-1.5 ${jobsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          
          {jobsLoading && !jobsData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : jobsData?.jobs ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schedule
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="When this job last successfully claimed a cron lock (i.e. ran in any replica).">
                      Last Run
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Number of distinct successful runs this job has had over the past hour. Helps spot silently-failing high-frequency jobs.">
                      Last Hour
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobsData.jobs.map((job, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{job.name}</div>
                          {job.requires_confirmation && (
                            <span className="ml-2" title="Requires confirmation to trigger">
                              <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                          {/* Info tooltip */}
                          <div className="relative ml-2 group">
                            <svg className="w-4 h-4 text-gray-400 hover:text-purple-600 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 left-full top-1/2 transform -translate-y-1/2 ml-2 w-72 p-3 text-xs bg-gray-900 rounded-lg shadow-xl">
                              <div className="font-semibold text-white mb-1 break-words">{job.name}</div>
                              <div className="text-gray-300 leading-relaxed break-words whitespace-normal">{jobDescriptions[job.id] || job.description || 'No description available'}</div>
                              <div className="absolute top-1/2 right-full transform -translate-y-1/2 mr-0">
                                <div className="border-4 border-transparent border-r-gray-900"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{job.schedule}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.type === 'Background Task' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {job.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          job.status === 'Running' 
                            ? 'bg-green-100 text-green-800' 
                            : job.status === 'Stopped'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          <span className={`w-2 h-2 mr-1.5 rounded-full ${
                            job.status === 'Running' 
                              ? 'bg-green-500' 
                              : job.status === 'Stopped'
                              ? 'bg-red-500'
                              : 'bg-yellow-500'
                          }`}></span>
                          {job.status}
                        </span>
                        {job.error && (
                          <p className="text-xs text-red-500 mt-1" title={job.error}>
                            Error: {job.error.length > 30 ? job.error.substring(0, 30) + '...' : job.error}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-testid={`job-last-run-${job.id}`}>
                        <span className={`text-sm ${job.last_claim_at ? 'text-gray-700' : 'text-gray-400'}`}
                          title={job.last_claim_at ? new Date(job.last_claim_at).toLocaleString('en-GB') : 'No record found in cron_locks'}>
                          {formatRelativeTime(job.last_claim_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-testid={`job-claims-last-hour-${job.id}`}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          job.claims_last_hour > 0
                            ? 'bg-green-50 text-green-700'
                            : (job.schedule || '').toLowerCase().includes('hour') || (job.schedule || '').toLowerCase().includes('daily') || (job.schedule || '').toLowerCase().includes('month')
                              ? 'bg-gray-100 text-gray-500'
                              : 'bg-amber-50 text-amber-700'
                        }`}
                        title="Successful runs in the last hour (one per replica that won the lock)">
                          {job.claims_last_hour ?? 0}×
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleTriggerJob(job)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                          data-testid={`trigger-job-${job.id}`}
                        >
                          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Run Now
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p>Unable to load scheduled jobs data</p>
              <button 
                onClick={fetchJobsStatus}
                className="mt-2 text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Job Trigger Confirmation Modal */}
      {triggerModal.isOpen && triggerModal.job && (
        <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="trigger-job-modal">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeModal}
            ></div>

            {/* Modal */}
            <div className="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  triggerModal.job.requires_confirmation ? 'bg-amber-100' : 'bg-purple-100'
                }`}>
                  {triggerModal.job.requires_confirmation ? (
                    <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {triggerModal.job.requires_confirmation ? 'Confirm Job Execution' : 'Run Job Now'}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You are about to manually trigger the <span className="font-semibold">{triggerModal.job.name}</span> job.
                    </p>
                    
                    {triggerModal.job.requires_confirmation && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex">
                          <svg className="h-5 w-5 text-amber-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <p className="text-sm text-amber-700">{triggerModal.job.warning}</p>
                        </div>
                      </div>
                    )}
                    
                    {triggerModal.job.requires_confirmation && (
                      <div className="mt-4">
                        <label htmlFor="confirmation-phrase" className="block text-sm font-medium text-gray-700">
                          Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-600">{triggerModal.job.confirmation_phrase}</span> to confirm:
                        </label>
                        <input
                          type="text"
                          id="confirmation-phrase"
                          value={triggerModal.confirmationInput}
                          onChange={(e) => setTriggerModal(prev => ({ ...prev, confirmationInput: e.target.value }))}
                          className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm font-mono"
                          placeholder="Type confirmation phrase..."
                          data-testid="confirmation-phrase-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={executeTrigger}
                  disabled={
                    triggerModal.isTriggering || 
                    (triggerModal.job.requires_confirmation && triggerModal.confirmationInput !== triggerModal.job.confirmation_phrase)
                  }
                  className={`w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                    triggerModal.job.requires_confirmation 
                      ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500' 
                      : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                  }`}
                  data-testid="confirm-trigger-button"
                >
                  {triggerModal.isTriggering ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Executing...
                    </>
                  ) : (
                    'Run Job'
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboardPage;
